import React from "react";
import { useAppStore } from "../utils/state";
import {
  Button,
  ColorInput,
  Input,
  Select,
  Slider,
  Textarea,
  TextInput,
} from "@mantine/core";

const demoImages = [
  "https://images.unsplash.com/photo-1479936343636-73cdc5aae0c3?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1566712473452-da271817b7be?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1655923689009-25677ef79cdd?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1646953225888-c87cebf47c30?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1598367899410-9e76064bb652?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.0.3",
  "https://images.unsplash.com/photo-1577023311546-cdc07a8454d9?q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.0.3",
];

const BuiltBy = () => (
  <div className="text-sm text-gray-600">
    Built by{" "}
    <a href="https://uicomet.com" target="_blank">
      UIComet
    </a>
  </div>
);

const SidePanel = () => {
  const {
    image,
    isLoading,
    text,
    setText,
    fontStyle,
    setFontStyle,
    processImage,
  } = useAppStore();

  const onDemoImageClick = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const file = new File([blob], "sample-image.jpg", { type: "image/jpeg" });
      processImage(file);
    } catch (error) {
      console.error("Error loading sample image:", error);
    }
  };

  if (!image || isLoading) {
    // Show help info about app
    return (
      <div className="space-y-4">
        <p>
          ImageType is a tool to place text behind images directly in your
          browser. This app leverages machine learning models to process media
          locally, ensuring your files never leave your device.
        </p>
        <ul className="text-sm ml-4 list-disc">
          <li>One-click text behind images</li>
          <li>Customizable text and downloadable</li>
          <li>Local, private, and fast processing</li>
        </ul>
        <p className="text-gray-600 text-sm border-t border-slate-300 pt-2">
          To get started, upload an image or paste an image from your clipboard.
        </p>

        <h2 className="font-bold text-sm">Or try these demo images:</h2>
        <div className="flex gap-2 flex-wrap">
          {demoImages.map((url, index) => (
            <img
              key={index}
              src={url}
              alt={`Demo Image ${index + 1}`}
              className="size-20 object-cover cursor-pointer"
              onClick={() => onDemoImageClick(url)}
            />
          ))}
        </div>

        <BuiltBy />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        label="Text"
        placeholder="Bold text"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <Input.Wrapper label="Alignment">
        <Button.Group>
          <Button
            variant={fontStyle.align === "left" ? "filled" : "default"}
            onClick={() =>
              setFontStyle({
                ...fontStyle,
                align: "left",
              })
            }
          >
            Left
          </Button>
          <Button
            variant={fontStyle.align === "center" ? "filled" : "default"}
            onClick={() =>
              setFontStyle({
                ...fontStyle,
                align: "center",
              })
            }
          >
            Center
          </Button>
          <Button
            variant={fontStyle.align === "right" ? "filled" : "default"}
            onClick={() =>
              setFontStyle({
                ...fontStyle,
                align: "right",
              })
            }
          >
            Right
          </Button>
        </Button.Group>
      </Input.Wrapper>
      <ColorInput
        label="Text Color"
        value={fontStyle.fill as string}
        onChange={(value) => {
          setFontStyle({
            ...fontStyle,
            fill: value,
          });
        }}
      />
      <Select
        label="Font Family"
        data={[
          "Kablammo",
          "Kranky",
          "Itim",
          "Caveat",
          "Poppins",
          "Inconsolata",
          "Playwrite IT Moderna",
        ]}
        value={fontStyle.fontFamily}
        onChange={(value) => {
          setFontStyle({
            ...fontStyle,
            fontFamily: value as string,
          });
        }}
      />
      <Input.Wrapper label="Stroke" mb={8}>
        <Slider
          color="black"
          marks={[
            { value: 0, label: "0" },
            { value: 5, label: "5" },
            { value: 10, label: "10" },
          ]}
          min={0}
          max={10}
          value={fontStyle.strokeWidth}
          onChange={(value) => {
            setFontStyle({
              ...fontStyle,
              strokeWidth: value,
            });
          }}
        />
      </Input.Wrapper>
      <ColorInput
        label="Stroke Color"
        value={fontStyle.stroke as string}
        onChange={(value) => {
          setFontStyle({
            ...fontStyle,
            stroke: value,
          });
        }}
      />

      <BuiltBy />
    </div>
  );
};

export default SidePanel;
